import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../components/Seo";
import { adminDashboard, adminLogout, adminMe } from "../lib/api";
import { fmtLongDate } from "../lib/dates";
import { AdminHeader } from "../components/AdminNav";

const STATUS_TONE = {
    new: "bg-emerald-100 text-emerald-800",
    read: "bg-slate-100 text-slate-700",
    replied: "bg-blue-100 text-blue-800",
    archived: "bg-[#E5E1DB] text-[#5C5855]",
};

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        adminMe().catch(() => navigate("/admin/login", { replace: true }));
    }, [navigate]);

    useEffect(() => {
        adminDashboard()
            .then(setData)
            .catch((err) => setError(err?.response?.data?.detail || "Could not load dashboard"));
    }, []);

    const linkCls = "text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A]";

    return (
        <main
            data-testid="page-admin-dashboard"
            className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16 md:py-24"
        >
            <Seo path="/admin/dashboard" title="Dashboard — Admin" description="Inquiries overview" noindex />
            <AdminHeader title="Dashboard" />

            {error && <p className="mt-6 text-red-700 text-sm">{error}</p>}
            {!data && !error && <p className="asc-overline mt-10">Loading…</p>}

            {data && (
                <div className="mt-10 space-y-12">
                    {/* Totals */}
                    <section
                        data-testid="dashboard-totals"
                        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
                    >
                        <Tile label="Today" value={data.totals.today} testid="tile-today" />
                        <Tile label="This week" value={data.totals.this_week} testid="tile-week" />
                        <Tile label="This month" value={data.totals.this_month} testid="tile-month" />
                        <Tile label="All time" value={data.totals.all_time} testid="tile-total" />
                    </section>

                    {/* Status */}
                    <section>
                        <p className="asc-overline mb-4">Status</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {Object.entries(data.status).map(([k, v]) => (
                                <div
                                    key={k}
                                    data-testid={`status-${k}`}
                                    className="bg-white border border-[#E5E1DB] px-5 py-4"
                                >
                                    <p className={`inline-block px-2 py-1 text-[10px] uppercase tracking-[0.18em] ${STATUS_TONE[k] || ""}`}>
                                        {k}
                                    </p>
                                    <p className="serif text-3xl mt-3 font-light">{v}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Trend chart */}
                    <TrendChart trend={data.trend_30d} />

                    {/* Latest inquiries */}
                    <section>
                        <div className="flex items-end justify-between mb-4">
                            <p className="asc-overline">Latest inquiries</p>
                            <Link
                                to="/admin/inquiries"
                                data-testid="dashboard-see-all"
                                className="text-[11px] uppercase tracking-[0.22em] text-[#1A1A1A] border-b border-[#1A1A1A] pb-1"
                            >
                                See all →
                            </Link>
                        </div>
                        {data.latest.length === 0 ? (
                            <p className="text-[#5C5855]" data-testid="dashboard-empty">No inquiries yet.</p>
                        ) : (
                            <ul data-testid="dashboard-latest" className="divide-y divide-[#E5E1DB] bg-white border border-[#E5E1DB]">
                                {data.latest.map((i) => (
                                    <li key={i.id} className="p-4 flex flex-wrap items-center gap-4">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[#1A1A1A] font-medium truncate">{i.name}</p>
                                            <p className="text-xs text-[#8C7A6B] truncate">
                                                {i.email}
                                                {i.company && <> · {i.company}</>}
                                            </p>
                                        </div>
                                        <span className="text-[10px] uppercase tracking-[0.18em] text-[#5C5855]">
                                            {(i.form_type || "contact").replace("-", " ")}
                                        </span>
                                        <span className={`px-2 py-1 text-[10px] uppercase tracking-[0.18em] ${STATUS_TONE[i.status || "new"] || ""}`}>
                                            {i.status || "new"}
                                        </span>
                                        <span className="text-xs text-[#5C5855]">{fmtLongDate(i.created_at)}</span>
                                        <Link
                                            to="/admin/inquiries"
                                            className="text-[10px] uppercase tracking-[0.2em] text-[#1A1A1A] hover:underline"
                                        >
                                            Open
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>
            )}
        </main>
    );
}

function Tile({ label, value, testid }) {
    return (
        <div data-testid={testid} className="bg-white border border-[#E5E1DB] px-6 py-6">
            <p className="asc-overline text-[#8C7A6B]">{label}</p>
            <p className="serif text-4xl lg:text-5xl mt-3 font-light">{value}</p>
        </div>
    );
}

function TrendChart({ trend }) {
    const max = useMemo(() => Math.max(1, ...trend.map((d) => d.count)), [trend]);
    return (
        <section>
            <p className="asc-overline mb-4">Inquiries — last 30 days</p>
            <div
                data-testid="dashboard-trend"
                className="bg-white border border-[#E5E1DB] px-5 py-6"
            >
                <div className="flex items-end gap-1 h-40 min-w-0 overflow-x-auto">
                    {trend.map((d) => (
                        <div
                            key={d.date}
                            title={`${d.date}: ${d.count}`}
                            className="w-3 md:w-4 shrink-0 bg-[#1A1A1A] hover:bg-[#3E3A37] transition-colors"
                            style={{ height: `${(d.count / max) * 100}%`, minHeight: 2 }}
                        />
                    ))}
                </div>
                <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] text-[#8C7A6B] mt-3">
                    <span>{trend[0]?.date}</span>
                    <span>{trend[trend.length - 1]?.date}</span>
                </div>
            </div>
        </section>
    );
}
